// The Workflows area's pure helpers: what a new spec starts as, graph
// edits that keep references whole, fan-out targets, a dependency-depth
// layering, how long a run took, the run form built from a workflow's
// parameters (or an earlier run's values: Run again), and the page's gating. The server's rules are in rules.ts
// (re-exported here); a run's status word and progress line are the whole
// area's (status.ts). No React here, so model.selftest.ts can check it.

import type { HandoverSpec, ParameterSpec, RunSummary, StageSpec, WorkflowSettings, WorkflowSpec, WorkflowSummary } from "@/api/workflows";
import { isLiveRunStatus } from "@/api/workflows";
import { formatDuration, pluralize, toDate } from "@/lib/format";

export const WHAT_IS_A_WORKFLOW = "A pipeline of stages, each done by a subagent, that Corvus runs and hands along; a stage can fan out over many items.";
export const NOT_OFFERED = "Making and running workflows is switched off for this workspace (Settings → General).";
export const NEEDS_SUBAGENTS = "Workflows need subagents switched on (Settings → General).";
export const SETTINGS_UNREAD = "Couldn't check the workspace settings, so workflows can be looked at but not made or run until they load.";

// The server's rules live in rules.ts; re-exported here for the pages that
// have always imported them from the model.
export {
  MAX_BRIEF,
  MAX_FAN_OUT,
  MAX_PARAMETERS,
  MAX_STAGE_MINUTES,
  MAX_STAGES,
  MIN_STAGE_MINUTES,
  PARAM_NAME_RE,
  SLUG_RE,
  STAGE_ID_RE,
  hasCycle,
  paramNameProblem,
  slugProblem,
  specProblems,
  stageIdProblem,
  stageProblems,
} from "./rules";

export const PARAM_TYPES: { value: ParameterSpec["type"]; label: string }[] = [
  { value: "string", label: "Text" },
  { value: "text", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "integer", label: "Whole number" },
  { value: "boolean", label: "Yes / no" },
  { value: "date", label: "Date" },
  { value: "list", label: "List" },
];

export const ON_FAILURE: { value: StageSpec["on_failure"]; label: string; hint: string }[] = [
  { value: "stop", label: "Stop the run", hint: "A failed branch fails the whole run." },
  { value: "skip", label: "Skip the branch", hint: "The branch and what depends on it are dropped; the rest goes on." },
  { value: "continue", label: "Carry on", hint: "The failure is handed on as a labelled gap." },
];

/**
 * What the Workflows page does for the workspace switches and its list:
 * `offer` the full page; `browse` the workflows that exist without New,
 * Run or Edit; `away` back Home (switched off and none to show); `wait`
 * while a read it depends on loads. Same shape as subagentsPageMode.
 * Settings that could not be read are not "off": the page stays closed
 * (`browse`) but is never sent away, so it can say so and retry.
 */
export function workflowsPageMode(p: {
  settingsLoading: boolean;
  settingsFailed: boolean;
  offered: boolean;
  listLoading: boolean;
  count: number | undefined;
}): "offer" | "browse" | "away" | "wait" {
  if (p.settingsLoading) return "wait";
  if (p.settingsFailed) return "browse";
  if (p.offered) return "offer";
  if (p.listLoading) return "wait";
  return p.count === 0 ? "away" : "browse";
}

// ─── ids ───

/** "Funding-rate arbitrage" → "funding-rate-arbitrage": a valid slug to start from. */
export function slugify(text: string): string {
  let s = text.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);
  s = s.replace(/-+$/, "");
  if (s && !/^[a-z]/.test(s)) s = `w-${s}`.slice(0, 48).replace(/-+$/, "");
  if (s.length === 1) s = `${s}-workflow`;
  return s;
}

// ─── new things ───

export const DEFAULT_SETTINGS: WorkflowSettings = { max_parallel: 4, default_stage_minutes: 45, notify: "telegram", email: null };

/** The server's defaults for the handover step (schema.HandoverSpec). */
export const DEFAULT_HANDOVER: HandoverSpec = {
  enabled: true,
  subagent: "research-writer",
  audience: "an investment committee",
  brief: "",
  style: null,
  time_limit_minutes: 45,
  resources: {},
};

export function emptySpec(): WorkflowSpec {
  return {
    slug: "",
    name: "",
    description: "",
    version: 1,
    template: null,
    parameters: [],
    sources: { integrations: [], corpora: [], project_id: null, files: [], data: [], notes: "" },
    settings: { ...DEFAULT_SETTINGS },
    handover: { ...DEFAULT_HANDOVER, resources: {} },
    schedule: null,
    stages: [],
  };
}

function freeId(prefix: string, taken: ReadonlySet<string>, sep = "-", start = taken.size + 1): string {
  for (let n = start; ; n++) {
    const id = `${prefix}${sep}${n}`;
    if (!taken.has(id)) return id;
  }
}

/**
 * A new stage after `after` (default: the last one): it depends on it, so
 * the pipeline stays a chain until told otherwise. Never "handover" (the
 * run's own last step).
 */
export function newStage(stages: readonly StageSpec[], after?: string | null): StageSpec {
  const taken = new Set([...stages.map((s) => s.id), "handover"]);
  const last = (after ? stages.find((s) => s.id === after) : null) ?? stages[stages.length - 1];
  return {
    id: freeId("stage", taken, "-", stages.length + 1),
    name: "",
    subagent: "",
    brief: "",
    depends_on: last ? [last.id] : [],
    fan_out: null,
    emits_items: false,
    inputs: { upstream: true, files: true },
    time_limit_minutes: null,
    on_failure: "stop",
    terminal: null,
    deliverables: "",
    dynamic_roles: false,
    resources: {},
    agent: null,
  };
}

export function newParameter(params: readonly ParameterSpec[]): ParameterSpec {
  return { name: freeId("param", new Set(params.map((p) => p.name)), "_"), type: "string", label: "", description: "", default: null, required: false, choices: [] };
}

/** `base`, else base-2, base-3…: the first slug not taken, cut to fit 48 characters. */
export function freeSlug(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = `-${n}`;
    const slug = `${base.slice(0, 48 - suffix.length).replace(/-+$/, "")}${suffix}`;
    if (!taken.has(slug)) return slug;
  }
}

/** Is `name` already a workflow's name (case and outer spaces aside)? */
export function nameTaken(name: string, workflows: readonly { name: string }[]): boolean {
  const key = name.trim().toLowerCase();
  return workflows.some((w) => w.name.trim().toLowerCase() === key);
}

/** A name for another copy that no workflow has yet: "Smoke pipeline 2", then 3… */
export function copyName(name: string, workflows: readonly { name: string; slug: string }[]): string {
  const slugs = new Set(workflows.map((w) => w.slug));
  for (let n = 2; ; n++) {
    const suffix = ` ${n}`;
    const candidate = `${name.slice(0, 80 - suffix.length).trimEnd()}${suffix}`;
    if (!nameTaken(candidate, workflows) && !slugs.has(slugify(candidate))) return candidate;
  }
}

/** A copy to save under a new slug (Duplicate). */
export function duplicateSpec(spec: WorkflowSpec): WorkflowSpec {
  return { ...structuredClone(spec), slug: slugify(`${spec.slug}-copy`), name: `${spec.name} (copy)`, version: 1, template: null };
}

// ─── graph edits that keep references whole ───

export function withStageRemoved(spec: WorkflowSpec, id: string): WorkflowSpec {
  return {
    ...spec,
    stages: spec.stages
      .filter((s) => s.id !== id)
      .map((s) => {
        const lost = s.fan_out?.over === `items:${id}`;
        // Dynamic roles need an items fan-out: they go with it.
        return { ...s, depends_on: (s.depends_on ?? []).filter((d) => d !== id), fan_out: lost ? null : s.fan_out, dynamic_roles: lost ? false : s.dynamic_roles };
      }),
  };
}

/**
 * A stage renamed, its dependants and fan-outs following. Only to a free
 * id: an empty one, or one another stage has, would merge the two stages
 * (every later edit hitting both), so that changes nothing. The inspector
 * says why before it gets here (rules.stageIdRenameProblem).
 */
export function withStageId(spec: WorkflowSpec, oldId: string, newId: string): WorkflowSpec {
  if (!newId || newId === oldId || spec.stages.some((s) => s.id === newId)) return spec;
  return {
    ...spec,
    stages: spec.stages.map((s) => ({
      ...s,
      id: s.id === oldId ? newId : s.id,
      depends_on: (s.depends_on ?? []).map((d) => (d === oldId ? newId : d)),
      fan_out: s.fan_out?.over === `items:${oldId}` ? { ...s.fan_out, over: `items:${newId}` } : s.fan_out,
    })),
  };
}

/** {{params.<name>}} as the engine reads it (schema.substitute: spaces allowed inside the braces). */
function paramRef(name: string): RegExp {
  return new RegExp(`\\{\\{\\s*params\\.${name.replace(/[^a-zA-Z0-9_]/g, "")}\\s*\\}\\}`, "g");
}

/** The texts a parameter can be referred to in: every brief and deliverables, and the handover's brief. */
function paramTexts(spec: WorkflowSpec): string[] {
  return [...spec.stages.flatMap((s) => [s.brief, s.deliverables ?? ""]), spec.handover?.brief ?? ""];
}

/** How many {{params.<name>}} the briefs, deliverables and the handover's brief hold. */
export function paramRefCount(spec: WorkflowSpec, name: string): number {
  return paramTexts(spec).reduce((n, t) => n + (t.match(paramRef(name))?.length ?? 0), 0);
}

/** The stages whose brief or deliverables refer to {{params.<name>}}. */
export function stagesUsingParam(spec: WorkflowSpec, name: string): StageSpec[] {
  const re = paramRef(name);
  return spec.stages.filter((s) => (s.brief.match(re)?.length ?? 0) + ((s.deliverables ?? "").match(re)?.length ?? 0) > 0);
}

/**
 * A parameter renamed, and everything that names it with it: the fan-outs
 * over it and every {{params.<old>}} in the briefs, deliverables and the
 * handover's brief (left as they were, they would reach the subagent as
 * literal braces). An empty or taken name changes nothing.
 */
export function withParamName(spec: WorkflowSpec, oldName: string, newName: string): WorkflowSpec {
  const params = spec.parameters ?? [];
  if (!newName || newName === oldName || params.some((p) => p.name === newName)) return spec;
  const re = paramRef(oldName);
  const swap = (text: string) => text.replace(re, `{{params.${newName}}}`);
  return {
    ...spec,
    parameters: params.map((p) => (p.name === oldName ? { ...p, name: newName } : p)),
    stages: spec.stages.map((s) => ({
      ...s,
      brief: swap(s.brief),
      deliverables: s.deliverables == null ? s.deliverables : swap(s.deliverables),
      fan_out: s.fan_out?.over === `param:${oldName}` ? { ...s.fan_out, over: `param:${newName}` } : s.fan_out,
    })),
    handover: spec.handover && spec.handover.brief != null ? { ...spec.handover, brief: swap(spec.handover.brief) } : spec.handover,
  };
}

// ─── fan-out ───

export interface FanOutTarget {
  value: string;
  label: string;
  group: string;
}

/** What a stage may fan out over: the list parameters and the other stages that emit items. */
export function fanOutTargets(spec: WorkflowSpec, stage: StageSpec): FanOutTarget[] {
  const params = (spec.parameters ?? []).filter((p) => p.type === "list").map((p) => ({ value: `param:${p.name}`, label: p.label || p.name, group: "List parameters" }));
  const items = spec.stages.filter((s) => s.id !== stage.id && s.emits_items).map((s) => ({ value: `items:${s.id}`, label: `Items of ${s.name || s.id}`, group: "Stage items" }));
  return [...params, ...items];
}

// ─── layering by dependency depth ───

/**
 * Stages grouped by dependency depth (a column per depth): a stage with no
 * dependencies is depth 0, else one past its deepest dependency. Unknown
 * dependencies are ignored and a cycle is cut where it closes, so a
 * half-edited spec still previews.
 */
export function stageLayers(stages: readonly StageSpec[]): StageSpec[][] {
  const byId = new Map(stages.map((s) => [s.id, s]));
  const depth = new Map<string, number>();
  const visiting = new Set<string>();
  const of = (id: string): number => {
    const known = depth.get(id);
    if (known !== undefined) return known;
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const s = byId.get(id);
    const deps = (s?.depends_on ?? []).filter((d) => d !== id && byId.has(d));
    const d = deps.length ? Math.max(...deps.map(of)) + 1 : 0;
    visiting.delete(id);
    depth.set(id, d);
    return d;
  };
  const layers: StageSpec[][] = [];
  for (const s of stages) {
    const d = of(s.id);
    (layers[d] ??= []).push(s);
  }
  return layers.filter((l) => l !== undefined);
}

// ─── runs ───

/** How long a run has been going, or took: "12 min", "1 h 5 min"; "" before it started. */
export function runElapsed(run: Pick<RunSummary, "started_at" | "ended_at" | "status">, now: Date): string {
  const start = toDate(run.started_at);
  if (!start) return "";
  const end = (isLiveRunStatus(run.status) ? null : toDate(run.ended_at)) ?? now;
  return formatDuration(Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000)));
}

/**
 * The data sources a workflow names that a run could not read (UX review
 * W1): gone from the workspace ("not connected any more"), or its
 * connection failed (status error). One being discovered, or new, is fine.
 * The server says the same on the card (WorkflowSummary.missing_data).
 */
export function dataProblems(
  names: readonly string[],
  sources: readonly { name: string; title?: string | null; status?: string | null }[],
): { name: string; text: string; gone: boolean }[] {
  const by = new Map(sources.map((s) => [s.name, s]));
  const out: { name: string; text: string; gone: boolean }[] = [];
  for (const n of names) {
    const s = by.get(n);
    if (!s) out.push({ name: n, text: `Data: “${n}” is not connected any more. Its steps would look for data that is not there.`, gone: true });
    else if (s.status === "error") out.push({ name: n, text: `Data: “${s.title || n}” cannot be reached (its last check failed). Its steps would find no data.`, gone: false });
  }
  return out;
}

/** The card's fact chips: "6 stages", "5 subagents", "2 fan-out", "4 parameters". */
export function workflowFacts(w: WorkflowSummary): string[] {
  return [
    pluralize(w.stages, "stage"),
    pluralize(w.subagents.length, "subagent"),
    ...(w.fan_out_stages ? [`${w.fan_out_stages} fan-out`] : []),
    ...(w.parameters ? [pluralize(w.parameters, "parameter")] : []),
  ];
}

// ─── the run form (RunParamsDialog) ───

export type RunFormValue = string | boolean | string[];
export type RunForm = Record<string, RunFormValue>;

function asList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === "string" && v.trim()) return v.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  return [];
}

/**
 * A value from an earlier run, typed for its parameter's control; undefined
 * when it is missing, empty or not valid for the parameter now (wrong type,
 * not a date, no longer one of the choices), so the default is used.
 */
function fromEarlierRun(p: ParameterSpec, v: unknown): RunFormValue | undefined {
  let typed: RunFormValue;
  if (p.type === "list") {
    if (typeof v === "string") typed = asList(v);
    else if (Array.isArray(v) && v.every((x) => typeof x === "string" || (typeof x === "number" && Number.isFinite(x)))) typed = v.map(String);
    else return undefined;
  } else if (p.type === "boolean") {
    if (typeof v !== "boolean") return undefined;
    typed = v;
  } else if (typeof v === "string" || (typeof v === "number" && Number.isFinite(v))) {
    typed = String(v);
  } else return undefined;
  if (typed === "" || (Array.isArray(typed) && typed.length === 0)) return undefined;
  return runFormProblems([p], { [p.name]: typed })[p.name] ? undefined : typed;
}

/**
 * The form's starting values: each parameter's default, typed for its
 * control; with `initial` (an earlier run's params, "Run again with these
 * settings"), that run's value wherever it is present and still valid.
 */
export function initialRunForm(params: readonly ParameterSpec[], initial?: Record<string, unknown>): RunForm {
  const out: RunForm = {};
  for (const p of params) {
    const earlier = initial && Object.prototype.hasOwnProperty.call(initial, p.name) ? fromEarlierRun(p, initial[p.name]) : undefined;
    if (earlier !== undefined) {
      out[p.name] = earlier;
      continue;
    }
    const d = p.default;
    if (p.type === "list") out[p.name] = asList(d);
    else if (p.type === "boolean") out[p.name] = d === true || d === "true";
    else out[p.name] = d == null ? "" : String(d);
  }
  return out;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Problems per parameter name; a required value missing, a number that is not one, a date that is not a date. */
export function runFormProblems(params: readonly ParameterSpec[], form: RunForm): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of params) {
    const v = form[p.name];
    const empty = v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
    if (empty) {
      if (p.required) out[p.name] = "Needed.";
      continue;
    }
    if (p.type === "number" && typeof v === "string" && Number.isNaN(Number(v))) out[p.name] = "Not a number.";
    if (p.type === "integer" && typeof v === "string" && !/^-?\d+$/.test(v.trim())) out[p.name] = "Not a whole number.";
    if (p.type === "date" && typeof v === "string" && !DATE_RE.test(v)) out[p.name] = "Use YYYY-MM-DD.";
    if (p.choices?.length && p.type !== "list" && typeof v === "string" && !p.choices.includes(v)) out[p.name] = "Not one of the choices.";
    if (p.choices?.length && p.type === "list" && Array.isArray(v)) {
      const bad = v.filter((x) => !p.choices!.includes(x));
      if (bad.length) out[p.name] = `Not among the choices: ${bad.join(", ")}.`;
    }
  }
  return out;
}

/** The body's `params`: typed values; an empty optional value is left out so the server's default applies. */
export function runParamsBody(params: readonly ParameterSpec[], form: RunForm): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of params) {
    const v = form[p.name];
    if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) continue;
    if (p.type === "number" || p.type === "integer") out[p.name] = Number(v);
    else if (p.type === "boolean") out[p.name] = v === true || v === "true";
    else if (p.type === "list") out[p.name] = asList(v);
    else out[p.name] = String(v);
  }
  return out;
}
